/**
 * WebRTC ICE servers for Live Coding.
 *
 * Production A/V across different networks REQUIRES TURN.
 * The browser fetches config from our backend:
 *   GET /api/webrtc/ice-servers
 *
 * Backend env (recommended — free Open Relay):
 *   METERED_TURN_CREDENTIALS_URL=https://<app>.metered.live/api/v1/turn/credentials?apiKey=<KEY>
 *   Sign up: https://www.metered.ca/tools/openrelay/
 *
 * Optional static TURN:
 *   TURN_URLS=turn:host:3478,turns:host:443
 *   TURN_USERNAME=...
 *   TURN_CREDENTIAL=...
 */

import proxy from '../api/axios';

const FALLBACK_STUN = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun.metered.ca:80' },
];

let cached = null;
let cacheAt = 0;
const CACHE_MS = 3 * 60 * 1000;

export const clearIceCache = () => {
  cached = null;
  cacheAt = 0;
};

/**
 * @returns {Promise<{ iceServers: object[], hasTurn: boolean, source?: string }>}
 */
export async function fetchIceConfig({ force = false } = {}) {
  if (!force && cached && Date.now() - cacheAt < CACHE_MS) {
    return cached;
  }

  try {
    const { data } = await proxy.get('/webrtc/ice-servers', {
      params: force ? { refresh: '1' } : undefined,
    });
    const iceServers = Array.isArray(data?.iceServers) ? data.iceServers : FALLBACK_STUN;
    cached = {
      iceServers,
      hasTurn: !!data?.hasTurn,
      source: data?.source || 'api',
    };
    cacheAt = Date.now();
    if (!cached.hasTurn) {
      console.warn(
        '[webrtc] Server returned no TURN — cross-network calls will likely fail. Set METERED_TURN_CREDENTIALS_URL on the backend.',
      );
    }
    return cached;
  } catch (err) {
    console.warn('[webrtc] Failed to load ice-servers from API:', err?.message || err);
    cached = { iceServers: FALLBACK_STUN, hasTurn: false, source: 'fallback' };
    cacheAt = Date.now();
    return cached;
  }
}

/** @deprecated use fetchIceConfig — kept for call-site compatibility */
export async function getIceServers() {
  const { iceServers } = await fetchIceConfig();
  return iceServers;
}

export function getDefaultRtcConfiguration(iceServers, { forceRelay = false } = {}) {
  return {
    iceServers: iceServers?.length ? iceServers : FALLBACK_STUN,
    iceCandidatePoolSize: 10,
    ...(forceRelay ? { iceTransportPolicy: 'relay' } : {}),
  };
}
